import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { FallbackProps } from "react-error-boundary";

type UnknownErrorDialogProps = {
  open: boolean;
  title?: string;
  description?: string;
  error?: Error | null;
  onConfirm: () => void;
};

export function UnknownErrorDialog({
  open,
  title = "Something went wrong",
  description = "An unexpected error occurred. We'll take you back to the home page.",
  error,
  onConfirm,
}: UnknownErrorDialogProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Light neutral background similar to the loading screen */}
      <div className="absolute inset-0 bg-background" />

      {/* Subtle decorative background inspired by the loading screen */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-14 left-10 w-2 h-2 bg-line-mid rounded-full animate-pulse" />
        <div className="absolute top-36 right-24 w-1 h-1 bg-line-dashed rounded-full animate-pulse delay-1000" />
        <div className="absolute bottom-28 left-1/3 w-1.5 h-1.5 bg-line-mid rounded-full animate-pulse delay-2000" />
        <div className="absolute bottom-16 right-16 w-2 h-2 bg-line-dashed rounded-full animate-pulse delay-500" />
      </div>

      {/* Centered panel */}
      <div className="relative h-full w-full flex items-center justify-center p-4">
        <div
          className="w-full max-w-3xl rounded-lg border bg-background/95 shadow-menu"
          data-testid="unknown-error-dialog"
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full bg-destructive/10 p-2 dark:bg-destructive/20">
                <AlertTriangle className="h-5 w-5 text-destructive dark:text-destructive/70" />
              </div>
              <h2 className="text-lg font-semibold">{title}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>

            {error && (
              <div className="mt-4">
                <Collapsible open={showDetails} onOpenChange={setShowDetails}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Error details
                    </p>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="unknown-error-show-details"
                      >
                        {showDetails ? "Hide" : "Show"}
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="mt-2 border rounded-md bg-muted/50">
                      <div className="max-h-[40vh] overflow-auto p-3">
                        <pre
                          className="text-xs whitespace-pre-wrap break-words leading-relaxed"
                          data-testid="unknown-error-details"
                        >
                          {error.stack || error.message}
                        </pre>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={onConfirm}>
                Dismiss
              </Button>
              <Button onClick={onConfirm}>Return to Home</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Drop-in fallback for react-error-boundary
export function UnknownErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleConfirm = () => {
    // Reset the boundary and send user home
    resetErrorBoundary();
    const safeTargets = [
      "/",
      "/dimensions",
      "/users",
      "/budget-editing",
      "/models",
    ];
    const next = safeTargets.find((p) => p !== location.pathname) ?? "/";
    navigate(next, { replace: true });
  };

  return (
    <UnknownErrorDialog
      open
      error={error}
      onConfirm={handleConfirm}
      description="An unexpected error occurred. You will be returned to the home page."
    />
  );
}
